import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

import {
  IBN_WIZARD_SCHRITTE,
  IbnWizardPfad,
  IbnWizardSchritt,
  IbnWizardSeite
} from './ibn-wizard-schritte';


export interface IbnWizardZustand {
  aktiv: boolean;
  index: number;

  /** Laufrichtung – bestimmt, wohin ein optionaler Schritt ohne Ziel übersprungen wird. */
  richtung: 1 | -1;

  /** Gewählter Weg nach der Auswahl im Projekt; davor null. */
  pfad: IbnWizardPfad | null;
}


const INAKTIV: IbnWizardZustand = {
  aktiv: false,
  index: 0,
  richtung: 1,
  pfad: null
};


/**
 * Zentraler Zustand des optionalen IBN-Assistenten.
 *
 * Der Assistent führt nur: er kennt die Seiten des normalen Ablaufs
 * (Projekt → Import → Messstellenliste → Messstelle) und zeigt dort auf die
 * vorhandenen Bedienelemente. Importiert, gespeichert und geprüft wird
 * ausschließlich über die Funktionen der Seiten selbst.
 *
 * Der Zustand lebt hier im Service und übersteht damit jede Navigation
 * innerhalb der Anwendung. Ein Seitenwechsel schaltet passend weiter
 * (z. B. nach erfolgreichem Import); wer den Ablauf verlässt, beendet ihn.
 *
 * Im Projekt verzweigt der Ablauf: wer „Neue Inbetriebnahme“ wählt, landet
 * im Import (Weg „neu“); wer eine vorhandene Inbetriebnahme öffnet, landet
 * in der Messstellenliste (Weg „bestehend“) – die Importschritte entfallen.
 */
@Injectable({ providedIn: 'root' })
export class IbnWizardService {

  readonly schritte: readonly IbnWizardSchritt[] = IBN_WIZARD_SCHRITTE;

  private readonly zustandSubject =
    new BehaviorSubject<IbnWizardZustand>(INAKTIV);

  readonly zustand$ =
    this.zustandSubject.asObservable();

  /** Zuletzt besuchte URL je Seite – Ziel für „Zurück“ über eine Seitengrenze. */
  private seitenUrls: Partial<Record<IbnWizardSeite, string>> = {};

  private static readonly SEITEN: ReadonlyArray<[RegExp, IbnWizardSeite]> = [
    [/^\/projekte\/\d+\/?$/, 'projekt'],
    [/^\/wartung\/inbetriebnahme\/import\/?$/, 'import'],
    [/^\/wartung\/inbetriebnahme\/liste\/\d+\/?$/, 'liste'],
    [/^\/wartung\/inbetriebnahme\/messstelle\/\d+\/?$/, 'messstelle']
  ];


  constructor(
    private router: Router,
    @Inject(DOCUMENT) private document: Document
  ) {

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd)
      )
      .subscribe(e => this.beiNavigation(e.urlAfterRedirects));
  }


  // ========================================================
  // ZUSTAND
  // ========================================================

  get zustand(): IbnWizardZustand {
    return this.zustandSubject.value;
  }


  get aktuellerSchritt(): IbnWizardSchritt | null {

    const zustand = this.zustand;

    return zustand.aktiv
      ? this.schritte[zustand.index] ?? null
      : null;
  }


  get istLetzterSchritt(): boolean {
    return this.zustand.index === this.schritte.length - 1;
  }


  /** Schritte des gewählten Weges; vor der Auswahl zählt der vollständige Ablauf. */
  get sichtbareSchritte(): IbnWizardSchritt[] {
    return this.schritte.filter(s => this.passtZumPfad(s));
  }


  get schrittNummer(): number {

    const schritt = this.aktuellerSchritt;

    return schritt
      ? this.sichtbareSchritte.indexOf(schritt) + 1
      : 0;
  }


  get schrittAnzahl(): number {
    return this.sichtbareSchritte.length;
  }


  aktuelleUrl(): string {
    return this.router.url;
  }


  aktuelleSeite(): IbnWizardSeite | null {
    return this.seiteVonUrl(this.router.url);
  }


  // ========================================================
  // BEDIENUNG
  // ========================================================

  starten(): void {

    this.seitenUrls = {};

    const seite = this.aktuelleSeite();

    if (seite) {
      this.seitenUrls[seite] = this.router.url;
    }

    this.setze(0, 1);
  }


  weiter(): void {

    const schritt = this.aktuellerSchritt;

    if (!schritt || !this.istBereit(schritt)) {
      return;
    }

    if (this.istLetzterSchritt) {
      this.beenden();
      return;
    }

    // Die Seite selbst führt die Aktion aus; der Seitenwechsel schaltet weiter.
    if (schritt.weiterKlicktZiel) {
      this.findeZiel(schritt.ziele)?.click();
      return;
    }

    const naechster = this.nachbar(this.zustand.index, 1);

    if (naechster >= 0) {
      this.setze(naechster, 1);
    }
  }


  kannZurueck(): boolean {

    const { aktiv, index, pfad } = this.zustand;

    if (!aktiv) {
      return false;
    }

    const vorherIndex = this.nachbar(index, -1);

    if (vorherIndex < 0) {
      return false;
    }

    const aktuell = this.schritte[index];
    const vorher = this.schritte[vorherIndex];

    // Hinter einen erfolgten Import geht es nicht zurück.
    if (aktuell.keinZurueck && pfad === 'neu') {
      return false;
    }

    return vorher.seite === aktuell.seite
      || !!this.seitenUrls[vorher.seite];
  }


  zurueck(): void {

    if (!this.kannZurueck()) {
      return;
    }

    const index = this.nachbar(this.zustand.index, -1);
    const vorher = this.schritte[index];

    this.setze(index, -1);

    if (vorher.seite !== this.aktuelleSeite()) {

      const url = this.seitenUrls[vorher.seite];

      if (url) {
        this.router.navigateByUrl(url);
      }
    }
  }


  /** Optionaler Schritt ohne Ziel – in Laufrichtung, notfalls vorwärts weiter. */
  ueberspringen(): void {

    const { aktiv, index, richtung } = this.zustand;

    if (!aktiv) {
      return;
    }

    const naechster = this.nachbar(index, richtung);

    if (naechster >= 0 && this.schritte[naechster].seite === this.schritte[index].seite) {
      this.setze(naechster, richtung);
      return;
    }

    const vorwaerts = this.nachbar(index, 1);

    if (vorwaerts >= 0) {
      this.setze(vorwaerts, 1);
      return;
    }

    this.beenden();
  }


  /** Setzt alles zurück; Overlay und Hervorhebung verschwinden sofort. */
  beenden(): void {

    this.seitenUrls = {};

    if (this.zustand.aktiv) {
      this.zustandSubject.next(INAKTIV);
    }

    // Ältere Links mit ?wizard=1 hinterlassen keine Spuren in der URL.
    const baum = this.router.parseUrl(this.router.url);

    if ('wizard' in baum.queryParams) {

      const { wizard, ...rest } = baum.queryParams;

      baum.queryParams = rest;

      this.router.navigateByUrl(baum, { replaceUrl: true });
    }
  }


  // ========================================================
  // ZIELE
  // ========================================================

  istBereit(schritt: IbnWizardSchritt): boolean {

    if (schritt.wartetAufSeitenwechsel) {
      return false;
    }

    if (schritt.weiterKlicktZiel) {
      return !!this.findeZiel(schritt.ziele);
    }

    if (schritt.bereitWenn) {
      return !!this.findeZiel([schritt.bereitWenn]);
    }

    return true;
  }


  /** Erstes sichtbare Element zu den Zielnamen (ausgeblendete Tabellen zählen nicht). */
  findeZiel(namen?: readonly string[]): HTMLElement | null {

    for (const name of namen ?? []) {

      const elemente =
        this.document.querySelectorAll<HTMLElement>(`[data-wizard-target="${name}"]`);

      for (const element of Array.from(elemente)) {

        if (element.getClientRects().length > 0) {
          return element;
        }
      }
    }

    return null;
  }


  // ========================================================
  // NAVIGATION
  // ========================================================

  private beiNavigation(url: string): void {

    if (!this.zustand.aktiv) {
      return;
    }

    const seite = this.seiteVonUrl(url);

    // Ablauf verlassen (z. B. Projektübersicht): Assistent endet.
    if (!seite) {
      this.beenden();
      return;
    }

    this.seitenUrls[seite] = url;

    const { index, pfad } = this.zustand;

    if (this.schritte[index].seite === seite) {
      return;
    }

    // Vorwärts: Importseite geöffnet, Import erfolgreich, Inbetriebnahme oder Messstelle geöffnet.
    // Vor der Auswahl passt jeder Weg – der erste Treffer legt ihn fest (siehe setze()).
    const vorwaerts = this.schritte.findIndex(
      (s, i) => i > index && s.seite === seite && this.passtZumPfad(s)
    );

    if (vorwaerts >= 0) {
      this.setze(vorwaerts, 1);
      return;
    }

    // Rückwärts: z. B. von der Messstelle zur Liste – nach einem Import nie zurück in den Import.
    for (let i = index - 1; i >= 0; i--) {

      if (pfad === 'neu' && this.schritte[i + 1].keinZurueck) {
        break;
      }

      const schritt = this.schritte[i];

      if (schritt.seite === seite && this.passtZumPfad(schritt)) {
        this.setze(i, -1);
        return;
      }
    }

    this.beenden();
  }


  private seiteVonUrl(url: string): IbnWizardSeite | null {

    const pfad = url.split(/[?#]/)[0];

    const treffer =
      IbnWizardService.SEITEN.find(([muster]) => muster.test(pfad));

    return treffer ? treffer[1] : null;
  }


  private passtZumPfad(
    schritt: IbnWizardSchritt,
    pfad: IbnWizardPfad | null = this.zustand.pfad
  ): boolean {

    return !(pfad === 'bestehend' && schritt.nurNeu);
  }


  /** Nächster Schritt des aktuellen Weges in der gegebenen Richtung, sonst -1. */
  private nachbar(index: number, richtung: 1 | -1): number {

    for (let i = index + richtung; i >= 0 && i < this.schritte.length; i += richtung) {

      if (this.passtZumPfad(this.schritte[i])) {
        return i;
      }
    }

    return -1;
  }


  /**
   * Setzt den Schritt und leitet den Weg ab: bis einschließlich der Auswahl
   * ist er offen, der erste Schritt dahinter legt ihn fest.
   */
  private setze(index: number, richtung: 1 | -1): void {

    const verzweigung =
      this.schritte.findIndex(s => s.verzweigung);

    let pfad = this.zustand.pfad;

    if (index <= verzweigung) {
      pfad = null;
    } else if (pfad === null) {
      pfad = this.schritte[index].nurNeu ? 'neu' : 'bestehend';
    }

    this.zustandSubject.next({
      aktiv: true,
      index,
      richtung,
      pfad
    });
  }
}
